import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import Modal from '~/components/Modal/Modal';
import { deliveryAddressStore } from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import { useModal } from '~/hooks/useModal';

import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { mainStore, userStore } from '~/stores';

import Icon from '../Icon/Icon';
import InputPhone from '../InputPhone/InputPhone';

import styles from './ModalOutOfZone.module.scss';

const termsUrls = [
  company.config.links.legals?.generalTradingPolicy || { link: '' },
  company.config.links.legals?.privacyPolicy || { link: '' },
];

const PHONE_MAX_LENGTH = 15 + 1;

export const ModalOutOfZone = observer(() => {
  const { t } = useTranslation();
  const { closeModal } = useModal();

  const [isLoading, setIsLoading] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [formErrors, setFormErrors] = useState({
    phone: '',
    email: '',
    terms: '',
  });

  const customerName =
    (userStore.personalData.firstName &&
      `${userStore.personalData.firstName} ${userStore.personalData.middleName} ${userStore.personalData.lastName}`) ||
    '';

  useEffect(() => {
    deliveryAddressStore.setOutOfZonePhone('+' + userStore.phone);
    deliveryAddressStore.setOutOfZoneEmail(userStore.email);
    deliveryAddressStore.setOutOfZoneName(customerName);
    setIsPhoneValid(true);
  }, [customerName]);

  const handleTermsChange = (checked: boolean) => {
    setIsTermsAccepted(checked);
    setFormErrors({ ...formErrors, terms: '' });
  };

  const handlePhoneValidate = (flag: boolean) => {
    setIsPhoneValid(flag);
    if (!flag) {
      setFormErrors({ ...formErrors, phone: t('common:invalidPhone') });
    } else {
      setFormErrors({ ...formErrors, phone: '' });
      analyticsService.track(AnalyticsEvent.LOGIN_PHONE_ENTERED, {});
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    deliveryAddressStore.setOutOfZoneEmail(value);
    if (value && mainStore.validateEmail(value)) {
      setFormErrors({ ...formErrors, email: '' });
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    deliveryAddressStore.setOutOfZoneName(value);
  };

  const handleClose = () => {
    closeModal();
    deliveryAddressStore.setIsOutOfZoneModalOpen(false);
    deliveryAddressStore.resetOutOfZoneForm();
    setFormErrors({
      phone: '',
      email: '',
      terms: '',
    });
  };

  const validateForm = () => {
    const errors = {
      phone: '',
      email: '',
      terms: '',
    };

    if (!deliveryAddressStore.outOfZonePhone || !isPhoneValid) {
      errors.phone = t('common:invalidPhone');
    }

    if (!deliveryAddressStore.outOfZoneEmail) {
      errors.email = `${t('outOfZone:emailLabel')} ${t('phrases:required')}`;
    } else if (!mainStore.validateEmail(deliveryAddressStore.outOfZoneEmail)) {
      errors.email = `${t('phrases:invalidEmail')}`;
    }

    if (
      !isTermsAccepted &&
      deliveryAddressStore.outOfZoneEmail &&
      deliveryAddressStore.outOfZonePhone
    ) {
      errors.terms = t('phrases:acceptTerms');
      mainStore.pushAlert('error', t('outOfZone:tickTeCheckboxes'));
    }

    setFormErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      await deliveryAddressStore.submitOutOfZoneInterest();
      closeModal();
    } catch {
      // Error is already handled in the store with mainStore.pushAlert
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isShow={true}
      className={styles.modal}
      onBackdropDismiss={handleClose}
    >
      <div className={styles.root__closeWr}>
        <span className={styles.root__close} onClick={handleClose}>
          <Icon type="close" size={24} />
        </span>
      </div>
      <div className={styles.content}>
        <h1 className={styles.title}>{t('outOfZone:modalTitle')}</h1>
        <p className={styles.description}>{t('outOfZone:modalDescription')}</p>

        <div className={styles.form}>
          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>
              {`${t('outOfZone:phoneLabel')} *`}
            </label>
            <InputPhone
              value={deliveryAddressStore.outOfZonePhone || userStore.phone}
              onChange={(value) =>
                deliveryAddressStore.setOutOfZonePhone(value)
              }
              onValidate={handlePhoneValidate}
              placeholder={t('outOfZone:phonePlaceholder')}
              label=""
              autoFocus={true}
              maxLength={PHONE_MAX_LENGTH}
              className={classes([
                styles.inputPhone,
                formErrors.phone && styles.inputError,
              ])}
            />
          </div>

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>
              {`${t('outOfZone:nameLabel')}`}
            </label>
            <input
              className={styles.input}
              data-input="out-of-zone-name"
              placeholder={t('outOfZone:namePlaceholder')}
              value={deliveryAddressStore.outOfZoneName || customerName}
              onChange={handleNameChange}
            />
          </div>

          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>
              {`${t('outOfZone:emailLabel')} *`}
            </label>
            <input
              className={`${styles.input} ${
                formErrors.email ? styles.inputError : ''
              }`}
              value={deliveryAddressStore.outOfZoneEmail || userStore.email}
              onChange={handleEmailChange}
              placeholder={t('outOfZone:emailPlaceholder')}
              type="email"
              required
            />
          </div>
        </div>

        <div className={styles.termsWrapper}>
          <label
            className={classes([
              'type control__checkbox',
              formErrors.terms && 'error',
            ])}
          >
            <input
              type="checkbox"
              className="type control__checkbox-input"
              checked={isTermsAccepted}
              onChange={(e) => handleTermsChange(e.target.checked)}
            />
            <span className="type control__checkbox-mark" />
            <span
              className={classes([
                styles.termsText,
                formErrors.terms && styles.error,
              ])}
            >
              <Trans
                i18nKey="phrases:agreeToCollection"
                components={termsUrls.map(({ link = '' }) => (
                  <Link to={link} key={link} />
                ))}
              />
            </span>
          </label>
        </div>

        <button
          className="button _primary med"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {t('outOfZone:submit')}
        </button>
      </div>
    </Modal>
  );
});
